import React, {useState} from 'react'
import clsx from 'clsx'
import type {FunctionComponent, HTMLAttributes, SyntheticEvent} from 'react'
import type {AffiliateLink, ProductImage} from '~/types'
import {useReportWidgetSeen} from '~/hooks/analytic-hooks'
import {CLICK_ID_PLACEHOLDER} from '~/utils/affiliation'

interface ImageGalleryProps extends HTMLAttributes<HTMLDivElement> {
  images?: ProductImage[]
  productTitle?: string
  handleCardClick: (
    _e: SyntheticEvent,
    index: number,
    affiliateLink: AffiliateLink,
    affiliateLinkWithClickId: string,
    itemName: string,
    redirectUrl: string,
    elementName: string,
    elementPlace: string,
  ) => void
  shouldForceTrackAnalytics: boolean
  trackSearchParams: string
  clickId: string
  affiliateLink: AffiliateLink
}
const ImageGallery: FunctionComponent<ImageGalleryProps> = ({
  images,
  productTitle,
  handleCardClick,
  shouldForceTrackAnalytics,
  trackSearchParams,
  clickId,
  affiliateLink,
  ...attributes
}) => {
  const [selectedImageIndex, setSelectedImageIndex] = useState(0)

  const productImage = images?.[selectedImageIndex]

  const affiliateLinkWithClickId = affiliateLink?.url?.replaceAll(CLICK_ID_PLACEHOLDER, clickId)

  const redirectUrl = shouldForceTrackAnalytics
    ? `/redirect-analytics?affiliateLink=${encodeURIComponent(affiliateLinkWithClickId)}&${trackSearchParams}`
    : affiliateLinkWithClickId

  const [widgetRef] = useReportWidgetSeen({
    widget_name: 'image-gallery',
    widget_place: 'top',
  })

  return images && images.length > 0 ? (
    <div {...attributes}>
      <div
        ref={widgetRef}
        className="flex h-[340px] w-[395px] flex-col items-center justify-center gap-4 overflow-hidden from-[#12CCD9]/10 via-[#D67DFF]/10 to-[#FF64DD]/10 desktop:rounded-[10px] desktop:bg-gradient-to-r tablet:h-[195px] tablet:!w-full tablet:flex-row-reverse tablet:justify-between tablet:gap-6 tablet:rounded-bl-2xl tablet:rounded-br-2xl tablet:py-2"
      >
        <a
          href={redirectUrl}
          onClick={(e) =>
            handleCardClick(
              e,
              0,
              affiliateLink,
              affiliateLinkWithClickId,
              productTitle || '',
              redirectUrl,
              'image-gallery',
              'top',
            )
          }
          target="_blank"
          rel="sponsored noreferrer"
          aria-label={`view ${productTitle}`}
          className="h-[240px] w-[276px] rounded-lg bg-white bg-blend-multiply tablet:flex tablet:h-[167px] tablet:w-full tablet:items-center tablet:justify-center"
        >
          <img
            src={productImage?.large || productImage?.small || ''}
            alt={productTitle}
            loading="eager"
            fetchpriority="high"
            decoding="async"
            className="h-[240px] w-[276px] object-scale-down bg-blend-multiply tablet:h-[167px] tablet:w-[192px]"
          />
        </a>
        <div className="no-scrollbar flex w-[276px] justify-start gap-1 overflow-x-scroll tablet:h-[167px] tablet:w-[50px] tablet:min-w-[50px] tablet:flex-col tablet:overflow-y-scroll">
          {images?.map(({small, large}, index) => (
            <img
              key={`${productTitle}-${index}`}
              src={small || large || ''}
              alt={productTitle}
              onClick={() => setSelectedImageIndex(index)}
              className={clsx(
                '!size-11 shrink-0 cursor-pointer rounded border-[1px] bg-white object-cover p-1 transition-all',
                index === selectedImageIndex
                  ? 'border-[#EB288F] opacity-100 tablet:border-[#0284C7]'
                  : 'border-gray-200 opacity-50',
              )}
            />
          ))}
        </div>
      </div>
    </div>
  ) : null
}

export default ImageGallery
